import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Button } from '@mui/material';

import { NCICApi } from '@/api';
import { ConfirmationDialog, FormRoot, Page, PageHeader } from '@/components';
import { LocalStorageItems } from '@/constants';
import { withLoader, WithLoaderProps } from '@/hocs';
import { ConfirmTypes, useToastStore } from '@/store';

import { FullNCICRecord, NCICDefaultFormValue } from '../data';
import { NCICFormPage } from './NCICFormPage';

const NCICSearchPage: FC<WithLoaderProps> = ({ showLoader, hideLoader }) => {
  const { updateToast } = useToastStore();

  const deviceUID = localStorage.getItem(LocalStorageItems.DeviceUID);
  const navigate = useNavigate();

  const [isRequestModalOpen, setIsRequestModalOpen] = useState<boolean>(false);

  const methods = useForm<FullNCICRecord>({
    defaultValues: NCICDefaultFormValue,
    mode: 'all',
  });
  const { handleSubmit, reset } = methods;

  const requestAccess = () => {
    setIsRequestModalOpen(false);
    NCICApi.createNCICSearchApproval({ deviceUID: deviceUID || '' })
      .then(() => {
        updateToast({
          open: true,
          type: ConfirmTypes.SUCCESS,
          message: 'Request for NCIC access submitted successfully',
        });
      })
      .catch((error) => {
        updateToast({
          open: true,
          type: ConfirmTypes.ERROR,
          message: error.message || 'Failed to submit NCIC access request',
        });
      });
  };

  const onSubmit = async (values: any) => {
    showLoader();
    NCICApi.sendRequest(values, deviceUID || '')
      .then((res) => {
        // eslint-disable-next-line no-console
        console.log('🚀 ~ NCICApi.sendRequest ~ res:', res);
        navigate('/ncic-search/history');
      })
      .catch((error) => {
        if (error.status === 412) {
          setIsRequestModalOpen(true);
          updateToast({
            open: true,
            type: ConfirmTypes.ERROR,
            message: error.message || 'Failed to submit NCIC search query',
          });
          return;
        }
        if (error.status === 400) {
          updateToast({
            open: true,
            type: ConfirmTypes.ERROR,
            message: error.message || 'Invalid NCIC search query',
          });
          return;
        }
        updateToast({
          open: true,
          type: ConfirmTypes.ERROR,
          message: error.message || 'Failed to submit NCIC search query',
        });
        return;
      })
      .finally(() => {
        reset();
        hideLoader();
      });
  };

  return (
    <Page title="NCIC Search">
      <PageHeader title="NCIC Search">
        <Button
          onClick={() => {
            navigate('/ncic-search/history');
          }}
          color="inherit"
          variant="outlined"
          size="large"
        >
          History
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="error"
          variant="contained"
          size="medium"
        >
          Search
        </Button>
      </PageHeader>
      <FormProvider {...methods}>
        <FormRoot>
          <NCICFormPage />
        </FormRoot>
      </FormProvider>
      <ConfirmationDialog
        title="Not authorized"
        open={isRequestModalOpen}
        description="This device is not authorized for NCIC access. A request will be submitted to your Agency Administrator for approval. Do you want to proceed?"
        confirmText="Yes, Proceed"
        cancelText="Cancel "
        onConfirm={() => requestAccess()}
        onCancel={() => {
          setIsRequestModalOpen(false);
        }}
      />
    </Page>
  );
};

export default withLoader(NCICSearchPage);
